<template>
  <div class="related-products" :class="rootClasses">
    <div class="related-products__header" v-if="headerText || showAllRoute">
      <div class="related-products__header-text">
        {{ headerText }}
      </div>
      <router-link
        v-if="showAllRoute"
        :to="showAllRoute"
        class="related-products__header-button">
        {{ showAllText }}
      </router-link>
    </div>
    <div class="related-products__container">
      <div class="related-products__tabs" v-if="showTabs">
        <ul class="related-products__tab-list">
          <li v-for="tab in tabs" :key="tab.id" class="related-products__tab-list-item">
            <button class="related-products__tab-button" :class="tabButtonClass(tab)" @click="setActiveTab(tab)">
              {{ tab.label }}
            </button>
          </li>
        </ul>
        <div class="related-products__see-all">
          <nuxt-link class="related-products__inline-button" :to="activeTab.route">
            See all
          </nuxt-link>
        </div>
      </div>
      <div v-if="loadingState === 1">
        Loading data...
      </div>
      <item-slider
        v-else-if="useSlider"
        class="related-products__item-slider"
        item-type="product"
        :injected-slides="searchResults"
        :slides-per-view="slidesPerView" />
      <related-posts v-else-if="activeTab.id === 'blog'" />
      <div
        v-else
        class="related-products__grid">
        <product-tile
          v-for="product in searchResults"
          :key="product.databaseId"
          :product-id="product.databaseId"
          :product-image="slideMediaItemUrl(product)"
          :default-currency="product.defaultCurrency"
          :product-link="product.uri"
          :product-name="product.name"
          :vendor-slug="product.vendor.slug"
          :vendor-name="product.vendor.name"
          :soundcloud-id="product.productSpecs?.productSoundcloudCode"
          :soundcloud-id-type="product.productSpecs?.soundcloudShortcode?.includes('/playlists/') ? 'playlist' : 'track'"
          :product-specs="product.productSpecs"
          :product-variations="product.variations?.nodes"
          :product-subscription-options="product.productSubscriptionOptions"
          :sale-badges="product.productSaleBadges" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ItemSlider from '~/components/blocks/ItemSlider.vue';
import { defineAsyncComponent, useRuntimeConfig } from '#imports';

const RelatedPosts = defineAsyncComponent(() => import('~/components/blocks/RelatedPosts.vue'));

export default {
  setup () {
    return {
      config: useRuntimeConfig(),
    };
  },
  props: {
    headerText: {
      type: String,
      default: 'What\'s new / What\'s trending?'
    },
    filterDays: String,
    filterSale: Boolean,
    productIds: {
      type: [Array, String],
      default: [],
    },
    productLimit: {
      type: [Number, String],
      default: 10,
    },
    searchCategory: String,
    searchTerms: Array,
    freeTextSearch: String,
    slidesPerView: {
      type: Number,
      default: 5,
    },
    useSlider: Boolean,
    showTabs: Boolean,
    prominentHeader: Boolean,
    showAllRoute: String,
    showAllText: {
      type: String,
      default: 'Explore all',
    },
    orderBy: {
      type: String,
      default: 'date',
    },
  },
  components: {
    RelatedPosts,
    ItemSlider,
  },
  data() {
    return {
      searchResults: [],
      activeTab: null,
      tabs: [
        {
          id: 'deals',
          label: 'Super Deals',
          route: '/shop/sound-effects?on-sale=true',
        }, {
          id: 'sound-effects',
          label: 'Sound Effects',
          route: '/shop/sound-effects',
        }, {
          id: 'software',
          label: 'Software',
          route: '/shop/software',
        }, {
          id: 'blog',
          label: 'Blog',
          route: '/blog',
        }
      ],
      loadingState: 0,
    }
  },
  watch: {
    productIds () {
      this.doSearch(1);
    },
  },
  created() {
    this.activeTab = this.tabs[0];
    this.doSearch();
  },
  computed: {
    rootClasses () {
      return {
        'related-products--prominent-header': this.prominentHeader,
      };
    },
  },
  methods: {
    tabButtonClass (tab) {
      return {
        'related-products__tab-button--active': this.activeTab.id === tab.id,
      };
    },
    setActiveTab (tab) {
      if (tab.id !== this.activeTab.id) {
        this.activeTab = tab;
        this.doSearch();
        this.searchResults = [];
      }
    },
    slideMediaItemUrl(slide) {
      if (! slide?.image?.mediaItemUrl) {
        return '' //@todo fallback product image
      }

      return slide.image.mediaItemUrl
    },
    async doSearch(isRefresh: boolean = false) {
      this.loadingState = isRefresh ? 0 : 1;
      const queryParams = [
        'type=product-related',
      ];
      const baseUrl = `${this.config.public.baseUrl}/wp-json/content-search/v1/search`

      if (this.showTabs) {
        queryParams.push(`search-for=${this.activeTab.id}`);
      }

      if (this.freeTextSearch) {
        queryParams.push(`query=${this.freeTextSearch}`);
      }

      if (this.searchTerms) {
        Object.keys(this.searchTerms).forEach((filterKey) => {
          const params = [];

          this.searchTerms[filterKey].forEach((filterItem) => {
            params.push(filterItem.slug);
          });

          queryParams.push(`${filterKey}=${params.join(',')}`);
        });
      }

      if (this.searchCategory) {
        queryParams.push(`category=${this.searchCategory}`);
      }

      // set related-products flag
      if (this.productIds.length) {
        let productIds = this.productIds ?? [];

        if (typeof productIds === 'object') {
          productIds = productIds.join(',')
        }

        queryParams.push(`related-products=${productIds}`);
      }

      // filter by days
      if (this.filterDays) {
        queryParams.push(`days=${this.filterDays}`);
      }

      // filter on sale
      if (this.filterSale) {
        queryParams.push('on-sale=true');
      }

      queryParams.push(`limit=${this.productLimit}`);
      queryParams.push(`order-by=${this.orderBy}`);

      const queryUrl = `${baseUrl}?${queryParams.join('&')}`;

      let data;

      try {
        const results = await fetch(queryUrl);
        data = await results.json();
      } catch (e) {
        console.error(e)
        return
      }

      if (data) {
        if (isRefresh) {
          // Find the item by index that has been added to cart
          const index = this.searchResults.findIndex((item) => this.productIds.includes(item.databaseId));

          if (index >= 0) {
            const relatedProductIds = this.searchResults.map((item) => item.databaseId);
            // Find one item in the response that is not already displayed
            const newProduct = data.results.find((item) => !relatedProductIds.includes(item.product.databaseId));

            setTimeout(() => {
              this.searchResults[index] = newProduct.product;
            }, 1500);
          }
        } else {
          this.searchResults = data.results.map((result) => result.product);
        }

        this.loadingState = 2;
      }
    },
  }
}
</script>

<style lang="scss">
@use "~/common/scss/button";
@use "~/common/scss/helpers";
@use "~/common/scss/mq";
@use "~/common/scss/typography";
@use "~/common/scss/variables";

.related-products {
  $root: &;
  $borderRadius: 4px;

  margin-bottom: 3rem;

  &__header {
    align-items: baseline;
    display: flex;
    justify-content: space-between;
    margin: 1rem 0;
  }

  &__header-text {
    font-weight: 600;

    #{$root}--prominent-header & {
      @include typography.h4();

      padding-top: 1rem;
      text-align: center;
    }
  }

  &__header-button {
    font-size: helpers.px-to-rem(14);
    font-weight: bold;
    text-decoration: none;
  }

  &__container {
    background-color: variables.$colorGreyED;
    border-radius: 5px;
    padding: 1.2rem;
  }

  &__tabs {
    display: grid;
    gap: .25rem;
    grid-template: "space tabs seeAll" auto / 1fr auto 1fr;
    padding: 0 0 1.25rem;
    width: 100%;
  }

  &__tab-list {
    display: grid;
    grid-area: tabs;
    grid-template: "1 2 3 4" auto / 1fr 1fr 1fr 1fr;
    list-style: none;
  }

  &__tab-button {
    background-color: variables.$colorGreyFA;
    border: 1px solid variables.$colorGreyD5;
    font-size: helpers.px-to-rem(14);
    height: 100%;
    padding: .5rem 1.5rem;
    transition: background-color .25s;
    width: 100%;

    &:hover,
    &:focus,
    &--active {
      background-color: variables.$colorGreyActive;
    }

    #{$root}__tab-list-item:first-child & {
      border-radius: $borderRadius 0 0 $borderRadius;
    }

    #{$root}__tab-list-item:last-child & {
      border-radius: 0 $borderRadius $borderRadius 0;
    }

    #{$root}__tab-list-item:not(:last-child) & {
      border-right: transparent;
    }
  }

  &__see-all {
    align-items: center;
    display: flex;
    grid-area: seeAll;
    justify-content: flex-end;
  }

  &__inline-button {
    color: variables.$colorBlack;
    font-size: helpers.px-to-rem(20);
    font-weight: bold;
    text-decoration: none;
    transition: color .25s;

    &:hover {
      color: variables.$colorOrange;
    }
  }

  &__grid {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(2, 1fr);
    justify-content: center;

    @include mq.aboveMobile() {
      gap: 2rem;
      grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
    }

    & .product-tile {
      width: 100%;

      &__cover {
        height: auto;
      }
    }
  }
}
</style>
